import { useEffect, useMemo, useState } from 'react';
import { Banner, Spinner } from './States';
import { useInventario } from '../hooks/useInventario';
import type { InventoryItem } from '../api/types';

const brl = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface Props {
  selecionados: string[];
  onChange: (assetIds: string[]) => void;
  /** Crédito da seleção, para a outra coluna filtrar pelo que cabe nele. */
  onTotal?: (totalBrl: number) => void;
  /** Mostrado acima da lista, para explicar o que a seleção faz em cada tela. */
  titulo: string;
  subtitulo: string;
}

/**
 * Lista o inventário do cliente já cotado e deixa escolher os itens.
 *
 * Itens sem cotação continuam visíveis, mas desativados e com o motivo à
 * mostra: sumir com eles faria o cliente achar que o inventário foi lido
 * errado, quando na verdade o item está travado ou fora do nosso catálogo.
 */
export function InventoryPicker({ selecionados, onChange, onTotal, titulo, subtitulo }: Props) {
  const { user, carregandoConta, loginUrl, data, erro, carregando, carregar } = useInventario();
  const [soNegociaveis, setSoNegociaveis] = useState(true);

  const visiveis = useMemo(() => {
    if (!data) return [];
    return soNegociaveis ? data.items.filter((i) => i.quoteBrl !== null) : data.items;
  }, [data, soNegociaveis]);

  const total = useMemo(() => {
    if (!data) return 0;
    const escolhidos = new Set(selecionados);
    return data.items.reduce((s, i) => (escolhidos.has(i.assetId) ? s + (i.quoteBrl ?? 0) : s), 0);
  }, [data, selecionados]);

  /**
   * Quanto os itens valem no mercado de origem.
   *
   * Conta só o que tem cotação. Somar o mercado do inventário inteiro e
   * comparar com o que pagamos por 34 dos 56 itens mostraria uma diferença
   * que não existe — pareceria que a loja paga muito menos do que paga.
   */
  const mercadoInteiro = useMemo(() => {
    if (!data) return 0;
    return data.items.reduce((s, i) => (i.quoteBrl !== null ? s + (i.refPriceBrl ?? 0) : s), 0);
  }, [data]);

  const mercadoSelecionado = useMemo(() => {
    if (!data) return 0;
    const escolhidos = new Set(selecionados);
    return data.items.reduce((s, i) => (escolhidos.has(i.assetId) ? s + (i.refPriceBrl ?? 0) : s), 0);
  }, [data, selecionados]);

  // O total sobe para a página, que usa como teto do "cabe no meu crédito".
  useEffect(() => {
    onTotal?.(total);
  }, [total, onTotal]);

  if (carregandoConta) return <Spinner label="Carregando a sua conta…" />;

  if (!user) {
    return (
      <div className="panel narrow">
        <h2>{titulo}</h2>
        <p className="muted">Entre com a Steam para vermos o seu inventário.</p>
        <a className="btn primary" href={loginUrl}>
          Entrar com a Steam
        </a>
      </div>
    );
  }

  if (user.needsOnboarding) {
    return (
      <div className="panel narrow">
        <h2>{titulo}</h2>
        <Banner kind="warn">
          Falta o seu trade link. É por ele que a negociação acontece — sem isso não dá para cotar.
        </Banner>
        <a className="btn primary" href="/cadastro">
          Completar cadastro
        </a>
      </div>
    );
  }

  function alternar(item: InventoryItem) {
    if (item.quoteBrl === null) return;
    onChange(
      selecionados.includes(item.assetId)
        ? selecionados.filter((id) => id !== item.assetId)
        : [...selecionados, item.assetId],
    );
  }

  return (
    <div className="panel">
      <h2>{titulo}</h2>
      <p className="muted">{subtitulo}</p>

      {erro && <Banner kind="error">{erro}</Banner>}
      {/* A Steam limita consultas por IP; quando recusa, mostramos o inventário
          de minutos atrás em vez de uma tela vazia — e dizemos isso. */}
      {data?.aviso && <Banner kind="warn">{data.aviso}</Banner>}
      {carregando && !data && <Spinner label="Lendo o seu inventário na Steam…" />}

      {data && (
        <>
          <div className="stat-row">
            <span>
              <b>{data.total}</b> itens
            </span>
            <span className="pos">
              <b>{data.negociaveis}</b> com cotação
            </span>
            {/* As duas pontas lado a lado: o que vale lá fora e o que sai
                daqui. Sem o primeiro número, o desconto é uma porcentagem
                sobre um valor que o cliente não vê. */}
            <span className="dim">
              mercado: <b>{brl(mercadoInteiro)}</b>
            </span>
            <span>
              pagamos: <b>{brl(data.totalQuoteBrl)}</b>
            </span>
            <span className="dim">({data.discountPct}% abaixo do mercado de origem)</span>
          </div>

          <div className="actions-row">
            <label className="check inline">
              <input
                type="checkbox"
                checked={soNegociaveis}
                onChange={(e) => setSoNegociaveis(e.target.checked)}
              />
              <span>Só itens com cotação</span>
            </label>
            <button
              className="btn ghost small"
              onClick={() => onChange(visiveis.filter((i) => i.quoteBrl !== null).map((i) => i.assetId))}
            >
              Selecionar todos
            </button>
            <button className="btn ghost small" onClick={() => onChange([])} disabled={selecionados.length === 0}>
              Limpar seleção
            </button>
            <button className="btn ghost small" onClick={() => void carregar(true)} disabled={carregando}>
              ↻ Atualizar inventário
            </button>
          </div>

          <div className="inv-grid">
            {visiveis.map((item) => {
              const escolhido = selecionados.includes(item.assetId);
              const bloqueado = item.quoteBrl === null;
              return (
                <button
                  key={item.assetId}
                  className={`inv-card ${escolhido ? 'on' : ''} ${bloqueado ? 'off' : ''}`}
                  onClick={() => alternar(item)}
                  disabled={bloqueado}
                  title={item.motivo ?? item.name}
                >
                  {item.iconUrl ? <img src={item.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
                  <b>{item.name}</b>
                  {bloqueado ? (
                    <span className="motivo">{item.motivo}</span>
                  ) : (
                    <>
                      <span className="valor">{brl(item.quoteBrl)}</span>
                      <span className="ref">mercado {brl(item.refPriceBrl)}</span>
                    </>
                  )}
                  {escolhido && <span className="check-mark">✓</span>}
                </button>
              );
            })}
          </div>

          {visiveis.length === 0 && (
            <p className="muted">
              Nenhum item com cotação. Itens travados por troca ou fora do nosso catálogo não entram.
            </p>
          )}

          <div className="sel-bar">
            <span>
              <b>{selecionados.length}</b> selecionado(s)
            </span>
            {/* O valor de mercado do que está marcado, para a conta ser a
                mesma do cabeçalho — só que da seleção. */}
            {selecionados.length > 0 && (
              <span className="sel-mercado dim">mercado {brl(mercadoSelecionado)}</span>
            )}
            <b className="sel-total">{brl(total)}</b>
          </div>
        </>
      )}
    </div>
  );
}
